import React from 'react';
import Image from 'next/image';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import HeaderProfileButton from './HeaderProfileButton';
import { Address, Cart, MemberCopy, UserMinus } from '@/components/ui/Icons';
import { useUserStore } from '@/stores/cutomer/user';
import { ChevronUpIcon, CurrencyEuroIcon } from '@heroicons/react/24/outline';
import useCustomerAuth from '@/hooks/useCustomerAuth';
import { isImpersonateCustomer } from '@/lib/utils/functions';

type Props = {
  closeMenu?: () => void;
};

export default function HeaderProfile({ closeMenu }: Props) {
  const user = useUserStore((state) => state.user);

  const t = useTranslations();

  const { handleCustomerLogout, removeCustomerAuth } = useCustomerAuth();

  const removeUser = useUserStore((state) => state.removeUser);

  const logout = () => {
    if (isImpersonateCustomer()) {
      removeCustomerAuth(removeUser);
    } else {
      handleCustomerLogout(removeUser);
    }
  };

  return (
    <Menu as="div" className="relative">
      {({ open }) => (
        <>
          <Menu.Button className="flex h-[50px] items-center gap-x-[14px] lg:ml-[35px] lg:w-[80px]">
            <div className="relative h-[41px] w-[41px] overflow-hidden rounded-full">
              <Image
                src={user?.avatar ? user.avatar : '/avatar.jpg'}
                alt={'avatar'}
                fill
              />
            </div>
            <div className="flex lg:hidden">
              {user?.first_name} {user?.last_name ?? null}
            </div>
            <ChevronUpIcon
              className={cn(
                'transfrom h-5 w-5 stroke-[3] transition-transform delay-75 ease-in',
                open ? 'rotate-0  text-primary' : 'rotate-180 text-neutral-400'
              )}
            />
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute left-0 z-50 flex w-[206px] flex-col rounded-[15px] bg-white px-[15px] pb-[15px] pt-[15px] shadow-box lg:right-0">
              <span className="mb-[7px] break-words pl-[10px] text-h6 font-bold text-title">
                {user?.first_name} {user?.last_name}
              </span>

              <div className="">
                <Menu.Item>
                  {({ close }) => (
                    <HeaderProfileButton
                      label={t('header.profile')}
                      icon={<MemberCopy />}
                      tab="profile"
                      close={() => {
                        close();
                        closeMenu && closeMenu();
                      }}
                    />
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ close }) => (
                    <HeaderProfileButton
                      label={t('header.order')}
                      icon={<Cart />}
                      tab="orders"
                      close={() => {
                        close();
                        closeMenu && closeMenu();
                      }}
                    />
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ close }) => (
                    <HeaderProfileButton
                      label={t('storefront.profile.cashback')}
                      icon={<CurrencyEuroIcon className={cn('h-6 w-6 ')} />}
                      tab="cashback"
                      close={() => {
                        close();
                        closeMenu && closeMenu();
                      }}
                    />
                  )}
                </Menu.Item>
                {/* <Menu.Item>
                  {({ close }) => (
                    <HeaderProfileButton
                      label={t('header.address')}
                      icon={<Address />}
                      tab="address"
                      close={() => {
                        close();
                        closeMenu && closeMenu();
                      }}
                    />
                  )}
                </Menu.Item> */}
                <Menu.Item>
                  {({ close }) => (
                    <button
                      className={cn(
                        'flex h-[35px] w-full items-center rounded-[30px] pl-[10px]'
                      )}
                      onClick={() => {
                        logout();
                        close();
                        closeMenu && closeMenu();
                      }}
                    >
                      <UserMinus className={cn('me-[15px] text-neutral-400')} />
                      <span className="text-paragraph">
                        {t('header.logout')}
                      </span>
                    </button>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
}
